'use client';

import { Action as ToastActionPrimitive, Close as ToastClosePrimitive, Description as ToastDescriptionPrimitive, Provider as ToastProvider, Root as ToastRoot, Title as ToastTitlePrimitive, Viewport as ToastViewport } from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import React from 'react';
const ToastViewPort = React.forwardRef<React.ElementRef<typeof ToastViewport>, React.ComponentPropsWithoutRef<typeof ToastViewport>>(({
  className,
  ...props
}, ref) => <ToastViewport ref={ref} className={cn('p-y fixed right-1 top-1 z-[10000] flex max-h-screen w-full flex-col-reverse gap-2 px-2 md:max-w-[330px]', className)} {...props} />);
ToastViewPort.displayName = ToastViewport.displayName;
const toastVariants = cva('group pointer-events-auto relative flex w-full items-center justify-between space-x-2 rounded-sm border p-[10px] px-4 pr-[26px] shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full', {
  variants: {
    variant: {
      default: 'border-[#0176fd] bg-[#c2edfe] text-[#172a32]',
      destructive: 'border-[#ff3f2b] bg-[#fee3e1] text-[#303030]',
      success: 'border-[#06a64e] bg-[#b6eacd] text-[#0a160f]',
      warning: 'border-[#f9a402] bg-[#ffe4c7] text-[#1f1b12]',
      error: 'border-[#ff3f2b] bg-[#fee3e1] text-[#303030]'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const progressColorMap: Record<string, string> = {
  default: '#0176fd',
  destructive: '#ff3f2b',
  success: '#06a64e',
  warning: '#f9a402',
  error: '#ff3f2b'
};
function ToastProgressBar({
  duration,
  color,
  onComplete
}: Readonly<{
  duration: number;
  color: string;
  onComplete?: () => void;
}>) {
  const barRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    // Measure the actual rendered width (includes the -1px bleed on each side)
    const fullWidth = el.getBoundingClientRect().width;
    el.style.width = `${fullWidth}px`;
    el.getBoundingClientRect(); // force reflow so transition fires
    el.style.width = '0px';
  }, []);
  return <div ref={barRef} onTransitionEnd={onComplete} style={{
    position: 'absolute',
    bottom: -1,
    left: -1,
    right: -1,
    height: '3px',
    backgroundColor: color,
    borderBottomLeftRadius: '2px',
    borderBottomRightRadius: '2px',
    transition: `width ${duration}ms linear`
  }} data-sentry-component="ToastProgressBar" data-sentry-source-file="toast.tsx" />;
}
const Toast = React.forwardRef<React.ElementRef<typeof ToastRoot>, React.ComponentPropsWithoutRef<typeof ToastRoot> & VariantProps<typeof toastVariants>>(({
  className,
  variant,
  duration,
  children,
  ...props
}, ref) => {
  const effectiveDuration = duration ?? 5000;
  const showProgress = Number.isFinite(effectiveDuration);
  const progressColor = progressColorMap[variant ?? 'default'] ?? progressColorMap.default;
  const {
    onOpenChange,
    ...restProps
  } = props;
  return <ToastRoot ref={ref} className={cn(toastVariants({
    variant
  }), className)} duration={duration} style={{
    overflow: 'visible'
  }} onOpenChange={onOpenChange} {...restProps}>
      {children}
      {showProgress && <ToastProgressBar duration={effectiveDuration} color={progressColor} onComplete={() => onOpenChange?.(false)} />}
    </ToastRoot>;
});
Toast.displayName = ToastRoot.displayName;
const ToastAction = React.forwardRef<React.ElementRef<typeof ToastActionPrimitive>, React.ComponentPropsWithoutRef<typeof ToastActionPrimitive>>(({
  className,
  ...props
}, ref) => <ToastActionPrimitive ref={ref} className={cn('inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-[15px] font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive', className)} {...props} />);
ToastAction.displayName = ToastActionPrimitive.displayName;
const ToastClose = React.forwardRef<React.ElementRef<typeof ToastClosePrimitive>, React.ComponentPropsWithoutRef<typeof ToastClosePrimitive>>(({
  className,
  ...props
}, ref) => <ToastClosePrimitive ref={ref} className={cn('absolute right-1 top-[50%] -translate-y-1/2 translate-x-[0] rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600', className)} toast-close="" {...props}>
    <X className="h-4 w-4" />
  </ToastClosePrimitive>);
ToastClose.displayName = ToastClosePrimitive.displayName;
const ToastTitle = React.forwardRef<React.ElementRef<typeof ToastTitlePrimitive>, React.ComponentPropsWithoutRef<typeof ToastTitlePrimitive>>(({
  className,
  ...props
}, ref) => <ToastTitlePrimitive ref={ref} className={cn('break-words text-[15px] font-semibold [&+div]:text-[14px]', className)} {...props} />);
ToastTitle.displayName = ToastTitlePrimitive.displayName;
const ToastDescription = React.forwardRef<React.ElementRef<typeof ToastDescriptionPrimitive>, React.ComponentPropsWithoutRef<typeof ToastDescriptionPrimitive>>(({
  className,
  ...props
}, ref) => <ToastDescriptionPrimitive ref={ref} className={cn('break-words text-[15px] font-medium leading-[1.1]', className)} {...props} />);
ToastDescription.displayName = ToastDescriptionPrimitive.displayName;
type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;
type ToastActionElement = React.ReactElement<typeof ToastAction>;
export { Toast, ToastAction, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewPort, type ToastActionElement, type ToastProps };